/**
 * Builds the `set_block_trade_context` payload — the visible UnifiedRfqs
 * trade-tape snapshot the AI agent uses to answer block-trade questions.
 * Shared by the background pump (`BlockTradeContextPump`, debounced) and the
 * `/analyze` drain (`PendingMessageDrain`, which pushes it synchronously
 * before the prompt so the agent has the tape when it answers).
 */

/** Cap the snapshot so the LLM context stays tight. */
export const MAX_TRADES = 50;

/**
 * Snapshot the latest `MAX_TRADES` visible trades into a gateway-request
 * payload. `ts` is stamped at call time; `visible_count` is the untruncated
 * total so the agent knows how much was dropped. Generic over the id type so
 * it matches the trade-tape service exactly (ids are `string | number`).
 *
 * Returns a plain `Record` (the gateway `request` param type); the trade
 * objects stay opaque to the host.
 */
export function buildBlockTradeContext<Id>(
  tradeIds: readonly Id[],
  service: { selectById(id: Id): unknown },
): Record<string, unknown> {
  const trades = tradeIds
    .slice(0, MAX_TRADES)
    .map((id) => service.selectById(id))
    .filter((t): t is NonNullable<typeof t> => t != null);

  return {
    source: 'paradigm.unified_rfqs.trade_tape',
    ts: Date.now(),
    visible_count: tradeIds.length,
    trades,
  };
}
